import {useMutation, useQueryClient} from "@tanstack/react-query";

import {notificationFeedService} from "../services";
import {useNotificationFeedStore} from "../store";
import type {NotificationItem, NotificationTopic} from "../types";
import {NOTIFICATION_FEED_QUERY_KEY} from "./use-notification-feed";

type PreviousFeedSnapshot = {
  items: NotificationItem[];
  unreadCount: number;
  meta: ReturnType<typeof useNotificationFeedStore.getState>["meta"];
  isHydrated: boolean;
};

type NotificationActionMutationInput =
  | {
      ids: string[];
    }
  | {
      allUnread: true;
      topic: NotificationTopic;
    };

type MutationContext = {
  previous: PreviousFeedSnapshot;
};

const takeSnapshot = (): PreviousFeedSnapshot => {
  const state = useNotificationFeedStore.getState();
  return {
    items: state.items,
    unreadCount: state.unreadCount,
    meta: state.meta,
    isHydrated: state.isHydrated,
  };
};

const resolveOptimisticIds = (input: NotificationActionMutationInput) => {
  if ("ids" in input) {
    return input.ids;
  }

  const state = useNotificationFeedStore.getState();
  return state.items
    .filter((item) => item.topic === input.topic && !item.read_at && !item.discarded_at)
    .map((item) => item.id);
};

const rollbackSnapshot = (snapshot: PreviousFeedSnapshot) => {
  useNotificationFeedStore.setState({
    items: snapshot.items,
    unreadCount: snapshot.unreadCount,
    meta: snapshot.meta,
    isHydrated: snapshot.isHydrated,
  });
};

const invalidateFeed = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({queryKey: [NOTIFICATION_FEED_QUERY_KEY]});

export function useNotificationActions() {
  const queryClient = useQueryClient();
  const {markReadLocal, discardLocal} = useNotificationFeedStore();

  const markReadMutation = useMutation({
    mutationFn: async (input: NotificationActionMutationInput) => {
      if ("ids" in input) {
        await notificationFeedService.markReadByIds(input.ids);
        return;
      }

      await notificationFeedService.markAllUnreadByTopic(input.topic);
    },
    retry: false,
    onMutate: async (input): Promise<MutationContext> => {
      const previous = takeSnapshot();
      const optimisticIds = resolveOptimisticIds(input);
      markReadLocal(optimisticIds);
      return {
        previous,
      };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        rollbackSnapshot(context.previous);
      }
      void invalidateFeed(queryClient);
    },
    onSettled: () => {
      void invalidateFeed(queryClient);
    },
  });

  const discardMutation = useMutation({
    mutationFn: async (input: NotificationActionMutationInput) => {
      if ("ids" in input) {
        await notificationFeedService.discardByIds(input.ids);
        return;
      }

      await notificationFeedService.discardAllUnreadByTopic(input.topic);
    },
    retry: false,
    onMutate: async (input): Promise<MutationContext> => {
      const previous = takeSnapshot();
      const optimisticIds = resolveOptimisticIds(input);
      discardLocal(optimisticIds);
      return {
        previous,
      };
    },
    onError: (_error, _variables, context) => {
      if (context) {
        rollbackSnapshot(context.previous);
      }
      void invalidateFeed(queryClient);
    },
    onSettled: () => {
      void invalidateFeed(queryClient);
    },
  });

  return {
    markReadByIds: (ids: string[]) => markReadMutation.mutate({ids}),
    discardByIds: (ids: string[]) => discardMutation.mutate({ids}),
    markAllUnreadByTopic: (topic: NotificationTopic) =>
      markReadMutation.mutate({allUnread: true, topic}),
    discardAllUnreadByTopic: (topic: NotificationTopic) =>
      discardMutation.mutate({allUnread: true, topic}),
    isMarkReadPending: markReadMutation.isPending,
    isDiscardPending: discardMutation.isPending,
    markReadError: markReadMutation.error,
    discardError: discardMutation.error,
  };
}
